/**
 * content-control — Office.js adapter for the per-position chip
 * binding. The only home (with custom-xml-part.ts) for `Word.run`
 * in the entire add-in (invariant W2).
 *
 * Implements (a superset of) ChipStoragePort for the domain layer.
 * The superset adds `updateStatusBatch` and a few helpers that the
 * domain port will gain in PR-3c when `Vérifier tout` lands.
 */
import { encode, parseTag, TagEncodeError } from '@/lib/tag-codec'
import type { CitationChip, ChipStatus } from '@/domain/citation-chip/entity'
import { OfficeAdapterError } from './errors'

/** Title used by every theplaidoyer chip — primary CC discriminator. */
const CC_TITLE = 'theplaidoyer-citation'

/** Chip color per ChipKind / status. Derived from CLAUDE.md §7 (FR
 *  jurisdictional blue, AI/JP gold, unverified orange, faits green,
 *  pieces violet). */
function colorForKind(kind: CitationChip['kind'], status: ChipStatus): string {
  if (status === 'unverified') return '#C97F4A' // orange
  if (status === 'broken-anchor') return '#E24B4A' // red
  if (status === 'stale') return '#94A3B8' // slate-400
  if (kind === 'jp') return '#C9A84C' // gold
  if (kind === 'article') return '#185FA5' // FR blue
  if (kind === 'piece') return '#534AB7' // violet (deprecated except as token)
  if (kind === 'fait') return '#1D9E75' // MA green
  return '#0D0D0F' // ink fallback
}

/**
 * Map a thrown Word.run error (`RichApi.Error`-shaped) into an
 * OfficeAdapterError. The mock raises `MockRichApiError` with `.code`;
 * real Office.js raises `OfficeExtension.Error` with `.code` — both
 * carry the same surface.
 */
function mapSyncError(err: unknown): OfficeAdapterError {
  const e = err as { code?: string; message?: string }
  if (e?.code === 'InvalidOperation') {
    return new OfficeAdapterError('document-closed', e.message ?? '', err)
  }
  if (e?.code === 'InvalidArgument') {
    return new OfficeAdapterError('no-selection', e.message ?? '', err)
  }
  if (e?.code === 'GeneralException') {
    return new OfficeAdapterError('sync-failed', e.message ?? '', err)
  }
  // Unknown — pass through as sync-failed (most defensive).
  return new OfficeAdapterError(
    'sync-failed',
    (err as Error)?.message ?? 'unknown',
    err,
  )
}

// ─── Public adapter shape ─────────────────────────────────────────

export interface ContentControlAdapter {
  /** Insert a new chip at the current selection (caret or range).
   *  `renderedText` is the lawyer-facing citation string (produced by
   *  domain/citation/renderCitation). */
  insert(chip: CitationChip, renderedText: string): Promise<void>

  /** Read all theplaidoyer chips currently in the doc. */
  list(): Promise<CitationChip[]>

  /** Update one chip's status (+ score + color) by cid. */
  updateStatus(cid: string, status: ChipStatus, score: number): Promise<void>

  /** Batched updates — one Word.run with one ctx.sync for all updates.
   *  Used by `Vérifier tout` (PR-3c). */
  updateStatusBatch(
    updates: ReadonlyArray<{
      cid: string
      status: ChipStatus
      score: number
    }>,
  ): Promise<void>
}

// ─── Office.js typing surface — minimal extract ───────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */
type WordCC = any
type WordCtx = any
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Type narrowing for the global `Word` symbol — installed by the
 *  Office.js runtime in production, by office-mock in tests. */
function getWord(): {
  run: <T>(batch: (ctx: WordCtx) => Promise<T>) => Promise<T>
} {
  const w = (globalThis as Record<string, unknown>).Word as
    | { run: <T>(batch: (ctx: WordCtx) => Promise<T>) => Promise<T> }
    | undefined
  if (!w) {
    throw new OfficeAdapterError(
      'sync-failed',
      'Word global not installed (running outside Word host / not mocked)',
    )
  }
  return w
}

// ─── Adapter factory ──────────────────────────────────────────────

export function createContentControlAdapter(): ContentControlAdapter {
  return {
    async insert(chip, renderedText) {
      let encoded: string
      try {
        encoded = encode(chip)
      } catch (err) {
        if (err instanceof TagEncodeError) {
          throw new OfficeAdapterError('tag-encode-failed', err.message, err)
        }
        throw err
      }
      const Word = getWord()
      try {
        await Word.run(async (ctx: WordCtx) => {
          const selection = ctx.document.getSelection()
          const cc: WordCC = selection.insertContentControl()
          cc.title = CC_TITLE
          cc.tag = encoded
          cc.appearance = 'Hidden'
          cc.color = colorForKind(chip.kind, chip.status)
          cc.cannotEdit = true
          // cannotDelete=false intentionally — with appearance=Hidden the
          // lawyer can silently remove a chip via backspace, which is the
          // expected UX (deleting a wrong cite is a common workflow).
          // PR-3b will surface a restore-from-history path if accidental
          // deletes turn out to be a real pain point.
          cc.cannotDelete = false
          cc.insertText(renderedText, 'Replace')
          await ctx.sync()
        })
      } catch (err) {
        if (err instanceof OfficeAdapterError) throw err
        throw mapSyncError(err)
      }
    },

    async list() {
      const Word = getWord()
      try {
        return await Word.run(async (ctx: WordCtx) => {
          const filtered = ctx.document.contentControls.getByTitle(CC_TITLE)
          filtered.load('items/id, items/tag, items/title')
          await ctx.sync()
          const chips: CitationChip[] = []
          for (const cc of filtered.items as WordCC[]) {
            const result = parseTag(cc.tag)
            if (!result.ok) {
              // Skip silently — list() is robust to corrupted chips.
              // Caller can opt into a verbose mode (PR-3c surfaces the
              // count in `Vérifier tout` footer).
              // eslint-disable-next-line no-console
              console.warn(
                `[content-control] skipping malformed tag on cc.id=${cc.id}: ${result.error.kind}`,
              )
              continue
            }
            chips.push(result.chip)
          }
          return chips
        })
      } catch (err) {
        if (err instanceof OfficeAdapterError) throw err
        throw mapSyncError(err)
      }
    },

    async updateStatus(cid, status, score) {
      const Word = getWord()
      try {
        await Word.run(async (ctx: WordCtx) => {
          const filtered = ctx.document.contentControls.getByTitle(CC_TITLE)
          filtered.load('items/id, items/tag, items/title')
          await ctx.sync()
          let found = false
          for (const cc of filtered.items as WordCC[]) {
            const result = parseTag(cc.tag)
            if (!result.ok) continue
            if (result.chip.cid !== cid) continue
            found = true
            const updated: CitationChip = {
              ...result.chip,
              status,
              score,
            }
            cc.tag = encode(updated)
            cc.color = colorForKind(updated.kind, status)
          }
          await ctx.sync()
          if (!found) {
            throw new OfficeAdapterError(
              'cid-not-found',
              `no theplaidoyer chip with cid=${cid}`,
            )
          }
        })
      } catch (err) {
        if (err instanceof OfficeAdapterError) throw err
        throw mapSyncError(err)
      }
    },

    async updateStatusBatch(updates) {
      const Word = getWord()
      const byCid = new Map(updates.map((u) => [u.cid, u]))
      try {
        await Word.run(async (ctx: WordCtx) => {
          const filtered = ctx.document.contentControls.getByTitle(CC_TITLE)
          filtered.load('items/id, items/tag, items/title')
          await ctx.sync()
          for (const cc of filtered.items as WordCC[]) {
            const result = parseTag(cc.tag)
            if (!result.ok) continue
            const update = byCid.get(result.chip.cid)
            if (!update) continue
            const updated: CitationChip = {
              ...result.chip,
              status: update.status,
              score: update.score,
            }
            cc.tag = encode(updated)
            cc.color = colorForKind(updated.kind, update.status)
          }
          await ctx.sync()
        })
      } catch (err) {
        if (err instanceof OfficeAdapterError) throw err
        throw mapSyncError(err)
      }
    },
  }
}

export { OfficeAdapterError }
